// frontend/src/context/AuthContext.js
import React, { createContext, useState, useEffect, useContext, useCallback } from 'react';
import * as authService from '../services/authService';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [authenticated, setAuthenticated] = useState(false);
  const [error, setError] = useState(null);

  // Initialize auth state
  useEffect(() => {
    const initAuth = async () => {
      // SECURITY: Check for authentication using httpOnly cookies
      try {
        const userData = await authService.getCurrentUser();
        setUser(userData);
        setAuthenticated(true);
        setError(null);
      } catch (error) {
        // Silently handle auth failures to prevent infinite reload loops
        console.log('User not authenticated:', error.message);
        setAuthenticated(false);
        setUser(null);
        setError(null); // Don't set error on initial load if not authenticated
      }

      setLoading(false);
    };
    
    initAuth();
  }, []);
  
  const login = async (email, password) => {
    setLoading(true);
    setError(null);
    
    try {
      const userData = await authService.login(email, password);
      setUser(userData);
      setAuthenticated(true);
      return userData;
    } catch (error) {
      console.error('Login failed:', error);
      setError(error.message || 'Authentication failed');
      throw error;
    } finally {
      setLoading(false);
    }
  };

  /** Reload user from /auth/me (new cookies/JWT after select-tenant, etc.). */
  const refreshUser = useCallback(async () => {
    authService.clearUserCache();
    try {
      const userData = await authService.getCurrentUser();
      setUser(userData);
      setAuthenticated(true);
      setError(null);
      return userData;
    } catch (e) {
      setAuthenticated(false);
      setUser(null);
      throw e;
    }
  }, []);
  
  const logout = () => {
    authService.logout();
    setAuthenticated(false);
    setUser(null);
    setError(null);
  };
  
  // Add a function to check if user has specific permission
  const hasPermission = (permission) => {
    if (!user || !user.permissions) return false;
    return user.permissions.includes(permission);
  };
  
  // Add a function to check if user has one of the specified roles
  const hasAnyRole = (roles) => {
    if (!user || !user.roles) return false;
    return roles.some(role => user.roles.includes(role));
  };
  
  return (
    <AuthContext.Provider value={{ 
      user, 
      authenticated, 
      loading, 
      error,
      hasPermission,
      hasAnyRole,
      login,
      refreshUser,
      logout 
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);

export default AuthContext;
